// Decimal-string based money formatting (§6 of the frontend proposal, §1.3 /
// §2 of the 2026-09-06 UX execution plan): the backend sends money as decimal
// strings, never float, and this is the only place formatting happens.
// Masking substitutes digits with bullets while preserving sign and grouping
// shape; it is a display mode only, never applied to exports. Negative signs
// render as U+2212 (minus sign), matching the design, not a hyphen.
import { createContext, createElement, useContext, useMemo, useState, type ReactNode } from 'react';

const MINUS_SIGN = '−'; // U+2212, not a hyphen-minus.

export interface FormatMoneyOptions {
  currency?: string;
  masked?: boolean;
  signDisplay?: 'auto' | 'always' | 'never';
}

/**
 * Format a decimal string (e.g. "-1234.5", "0.00") as a currency amount.
 * Never accepts a float: the API contract is a decimal string end to end.
 */
export function formatMoney(value: string | null, options: FormatMoneyOptions = {}): string {
  if (value === null) return '—';
  const { currency = 'USD', masked = false, signDisplay = 'auto' } = options;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    signDisplay,
  });
  const formatted = formatter.format(numeric).replace('-', MINUS_SIGN);
  if (!masked) return formatted;

  return maskFormatted(formatted);
}

/**
 * Digit-substituted masked form: "-$1,234.56" -> "−$•,•••.••". Preserves the
 * sign and the grouping shape (commas, decimal point) -- only digits are
 * replaced, per the design's `−$•,•••.••` idiom. Never the coarse "$••••".
 */
function maskFormatted(formatted: string): string {
  return formatted.replace(/\d/g, '•');
}

/** Masks a raw numeric string for chart tick/tooltip use, same substitution rule. */
export function maskAmount(formatted: string): string {
  return maskFormatted(formatted);
}

export interface FormatDeltaResult {
  glyph: '▲' | '▼' | '–';
  text: string;
}

/**
 * `▲ +$312.40 (+0.6%)` / `▼ −$1,248.10 (−2.3%)` -- the arrow is never the
 * only signal; the sign prefix is always present too.
 */
export function formatDelta(
  value: string,
  options: { percent?: string; currency?: string; masked?: boolean } = {},
): FormatDeltaResult {
  const { percent, currency, masked } = options;
  const numeric = Number(value);
  const glyph = numeric > 0 ? '▲' : numeric < 0 ? '▼' : '–';
  const amount = formatMoney(value, { currency, masked, signDisplay: 'always' });
  if (percent === undefined) {
    return { glyph, text: `${glyph} ${amount}` };
  }
  const percentNumeric = Number(percent);
  const percentSign = percentNumeric > 0 ? '+' : percentNumeric < 0 ? MINUS_SIGN : '';
  const percentMagnitude = masked ? '••' : Math.abs(percentNumeric).toFixed(1);
  const percentText = `${percentSign}${percentMagnitude}%`;
  return { glyph, text: `${glyph} ${amount} (${percentText})` };
}

/** `⇄ $2,400.00` -- transfers and card payments carry no sign; identity, never a loss. */
export function formatTransfer(value: string, options: { currency?: string; masked?: boolean } = {}): string {
  const amount = formatMoney(value, { ...options, signDisplay: 'never' });
  const magnitude = amount.replace(MINUS_SIGN, '').replace('-', '');
  return `⇄ ${magnitude}`;
}

export interface MoneyFormatConfig {
  currency: string;
  masked: boolean;
  timezone: string;
  setMasked: (masked: boolean) => void;
}

const MoneyFormatContext = createContext<MoneyFormatConfig | null>(null);

export function MoneyFormatProvider({
  children,
  currency = 'USD',
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
}: {
  children: ReactNode;
  currency?: string;
  timezone?: string;
}) {
  const [masked, setMasked] = useState(() => {
    try {
      return localStorage.getItem('moneyskyline.masked') === 'true';
    } catch {
      return false;
    }
  });

  const setMaskedPersisted = (next: boolean) => {
    setMasked(next);
    try {
      localStorage.setItem('moneyskyline.masked', String(next));
    } catch {
      // Per-device convenience only; ignore storage failures.
    }
  };

  const value = useMemo(
    () => ({ currency, masked, timezone, setMasked: setMaskedPersisted }),
    [currency, masked, timezone],
  );

  return createElement(MoneyFormatContext.Provider, { value }, children);
}

export function useMoneyFormat(): MoneyFormatConfig {
  const ctx = useContext(MoneyFormatContext);
  if (!ctx) {
    // Honest default outside a provider (e.g. isolated component tests) --
    // never throws, never masks unexpectedly.
    return { currency: 'USD', masked: false, timezone: 'UTC', setMasked: () => {} };
  }
  return ctx;
}
